/**
 * Single source of truth for the locales the platform recognizes.
 *
 * Used by:
 *   - account-language.tsx (admin UI language preference)
 *   - space translation settings (source + targets multi-select)
 *   - public LanguageToggle (Fumadocs slot)
 *
 * Codes match `apps/client/public/locales/<code>/translation.json` so the
 * admin UI strings stay in lockstep with the same identifier the public
 * renderer uses for content language URL prefixes.
 */
export interface SupportedLanguage {
  code: string;
  name: string;
  /** Country/region flag emoji. Falls back to plain text on platforms
   *  without flag fonts (older Windows). */
  flag: string;
}

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = [
  { code: "en-US", name: "English (US)", flag: "🇺🇸" },
  { code: "es-ES", name: "Español", flag: "🇪🇸" },
  { code: "de-DE", name: "Deutsch", flag: "🇩🇪" },
  { code: "fr-FR", name: "Français", flag: "🇫🇷" },
  { code: "nl-NL", name: "Nederlands", flag: "🇳🇱" },
  { code: "pl-PL", name: "Polski", flag: "🇵🇱" },
  { code: "pt-BR", name: "Português (BR)", flag: "🇧🇷" },
  { code: "it-IT", name: "Italiano", flag: "🇮🇹" },
  { code: "ja-JP", name: "日本語", flag: "🇯🇵" },
  { code: "ko-KR", name: "한국어", flag: "🇰🇷" },
  { code: "uk-UA", name: "Українська", flag: "🇺🇦" },
  { code: "ru-RU", name: "Русский", flag: "🇷🇺" },
  { code: "zh-CN", name: "中文 (简体)", flag: "🇨🇳" },
];

export const SUPPORTED_LANGUAGE_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

export function findLanguage(code: string): SupportedLanguage | undefined {
  return SUPPORTED_LANGUAGES.find((l) => l.code === code);
}
